import { RefObject, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { format, differenceInDays } from 'date-fns';
import {
  Filters,
  MenuItems,
  Pagination,
  PermissionEnum,
  SchemaFilterType,
  SelectItemProps,
  SortDir,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useAppDispatch } from '@store';
import { useLazyGetAccountsCategoriesQuery } from '../../../charts/store/services/categories';
import {
  useExportAccountsMutation,
  useGetAccountsValidatorsQuery,
  useLazyGetAccountsListQuery,
  useLazyGetAccountsStatsQuery,
  useLazyGetAccountsBalancesQuery,
} from '../../store/services';
import { columns, filtersSchema, schema } from '../variables/list';
import { clearAccountsState } from '../../store/slice';
import { Account } from '../../types';

const useListFunctions = ({
  scrollRef,
  pagination,
  overdraftEnabled,
  currencies,
  accounts,
  setShowBalances,
  search,
  accountsStateInititate,
  setFormattedColumns,
  showBalances,
  categoryId,
  groupId,
  primaryId,
  menuItems,
  activePermissions,
  closingBalancesAt,
  setClosingBalancesAt,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  showBalances?: boolean;
  categoryId: string | null;
  groupId: string | null;
  primaryId: string | null;
  menuItems?: MenuItems[];
  activePermissions?: PermissionEnum[];
  pagination?: Pagination;
  overdraftEnabled?: boolean;
  accounts?: Account[];
  setFormattedColumns: (
    data: {
      name: string;
      label: string;
      hidden: boolean;
      sortable?: boolean;
    }[],
  ) => void;
  accountsStateInititate?: boolean;
  setShowBalances: (data: boolean) => void;
  search?: string;
  currencies?: SelectItemProps[];
  closingBalancesAt?: string;
  setClosingBalancesAt: (data: string) => void;
}) => {
  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevMenuItems = usePrevious(menuItems);
  const prevPagination = usePrevious(pagination);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useGetAccountsValidatorsQuery();

  const [
    exportAccounts,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportAccountsMutation();
  const [
    getAccountsBalances,
    {
      isLoading: accountsBalancesIsLoading,
      isFetching: accountsBalancesIsFetching,
    },
  ] = useLazyGetAccountsBalancesQuery();

  const [
    getAccountsCategories,
    {
      isLoading: accountsCagoriesIsLoading,
      isFetching: accountsCategoriesIsFetching,
    },
  ] = useLazyGetAccountsCategoriesQuery();

  const [getAccountsStats] = useLazyGetAccountsStatsQuery();

  const getAccountsStatsAction = useCallback(() => {
    getAccountsStats();
  }, []);

  const [getAccountsList, { isFetching, isLoading }] =
    useLazyGetAccountsListQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<Account>({
      getList: getAccountsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  const onGetAccountsBalances = async ({
    checked,
    date,
  }: {
    checked: boolean;
    date?: string;
  }) => {
    setShowBalances(checked);

    if (date) {
      setClosingBalancesAt(date);
    } else if (closingBalancesAt && !date) {
      setClosingBalancesAt('');
    }

    let link = search?.replace('?', '');
    let query = '';

    if (accounts) {
      const ids = accounts.map((item) => item.id);

      if (checked) {
        query = `${query}${
          query?.includes('?') ? '&' : '?'
        }accountId=${ids.join('&accountId=')}&date=${new Date(
          date || new Date(),
        ).toISOString()}`;

        link = link
          ?.split('&')
          .filter((item) => !item.includes('includes'))
          .join('&');

        link = `${link}&includes=BALANCES`;
      } else {
        link = link
          ?.split('&')
          .filter((item) => !item.includes('includes'))
          .join('&');

        if (closingBalancesAt) {
          setClosingBalancesAt('');
        }
      }

      if (date) {
        link = link
          ?.split('&')
          .filter((item) => !item.includes('closingBalancesAt'))
          .join('&');

        link = `${link}&closingBalancesAt=${format(
          new Date(date || closingBalancesAt || new Date()),
          'yyyy-MM-dd',
        )}`;
      } else {
        link = link
          ?.split('&')
          .filter((item) => !item.includes('closingBalancesAt'))
          .join('&');
      }

      navigate(`?${link}`);

      let daysDiff = 0;

      if (query?.length) {
        if (date) {
          daysDiff = differenceInDays(new Date(), new Date(date));
        }

        if (daysDiff === 0) {
          await getAccountsBalances(query);
        } else if (daysDiff > 0) {
          const response = await getAccountsBalances(
            `?accountId=${ids.join(
              '&accountId=',
            )}&date=${new Date().toISOString()}`,
          );

          if (response) {
            await getAccountsBalances(query);
          }
        }
      }
    }
  };

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string | number> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (
          item !== 'cursor' &&
          item !== 'closingBalancesAt' &&
          item !== 'includes'
        ) {
          if (
            item === 'accountType' ||
            item === 'userId' ||
            item === 'organizationId' ||
            item === 'ids'
          ) {
            exportFilters[item] = filters[item].value;
          } else {
            const [filterValue] = filters[item].value;

            exportFilters[item] = filterValue;
          }
        }
      });
    }

    if (closingBalancesAt) {
      exportFilters.date = closingBalancesAt;
    }

    if (categoryId) {
      exportFilters.categoryId = [categoryId];
    }

    if (groupId) {
      exportFilters.groupId = [groupId];
    }

    if (primaryId) {
      exportFilters.primaryId = [primaryId];
    }

    // exportFilters.sortProp = [sort.sortProp] as string[];
    // exportFilters.sortDir = [sort.sortDir] as string[];

    await exportAccounts(exportFilters);
  };

  const onMenuCategoryClick = (id: string) => {
    const nextFilters: Filters = {
      ...filters,
      sortProp: {
        value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
        type: SchemaFilterType.STRING,
      },
      sortDir: {
        value: [sort.sortDir || SortDir.DESC],
        type: SchemaFilterType.STRING,
      },
    };

    delete nextFilters.groupId;
    delete nextFilters.primaryId;

    if (id === categoryId && !filters.groupId && !filters.primaryId) {
      delete nextFilters.categoryId;
    } else {
      nextFilters.categoryId = {
        value: [id],
        type: SchemaFilterType.STRING,
      };
    }

    if (showBalances) {
      delete nextFilters.includes;
    }

    if (closingBalancesAt) {
      delete nextFilters.closingBalancesAt;
    }

    onFilter(nextFilters);
  };

  const onMenuGroupClick = ({
    menuCategoryId,
    id,
  }: {
    menuCategoryId: string;
    id: string;
  }) => {
    const nextFilters: Filters = {
      ...filters,
      categoryId: {
        value: [menuCategoryId],
        type: SchemaFilterType.STRING,
      },
      sortProp: {
        value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
        type: SchemaFilterType.STRING,
      },
      sortDir: {
        value: [sort.sortDir || SortDir.DESC],
        type: SchemaFilterType.STRING,
      },
    };

    delete nextFilters.primaryId;

    if (groupId === id && !filters.primaryId) {
      delete nextFilters.groupId;
    } else {
      nextFilters.groupId = {
        value: [id],
        type: SchemaFilterType.STRING,
      };
    }

    if (showBalances) {
      delete nextFilters.includes;
    }

    if (closingBalancesAt) {
      delete nextFilters.closingBalancesAt;
    }

    onFilter(nextFilters);
  };

  const onMenuPrimaryAccountClick = ({
    menuCategoryId,
    menuGroupId,
    id,
  }: {
    menuCategoryId: string;
    menuGroupId: string;
    id: string;
  }) => {
    const nextFilters: Filters = {
      ...filters,
      categoryId: {
        value: [menuCategoryId],
        type: SchemaFilterType.STRING,
      },
      groupId: {
        value: [menuGroupId],
        type: SchemaFilterType.STRING,
      },
      sortProp: {
        value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
        type: SchemaFilterType.STRING,
      },
      sortDir: {
        value: [sort.sortDir || SortDir.DESC],
        type: SchemaFilterType.STRING,
      },
    };

    if (primaryId === id) {
      delete nextFilters.primaryId;
    } else {
      nextFilters.primaryId = {
        value: [id],
        type: SchemaFilterType.STRING,
      };
    }

    if (showBalances) {
      delete nextFilters.includes;
    }

    if (closingBalancesAt) {
      delete nextFilters.closingBalancesAt;
    }

    onFilter(nextFilters);
  };

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onTableFilterChange = (values: Filters) => {
    const nextFilters: Filters = {
      ...values,
      sortProp: {
        value: sort.sortProp ? [sort.sortProp] : ['fullNumber'],
        type: SchemaFilterType.STRING,
      },
      sortDir: {
        value: [sort.sortDir || SortDir.DESC],
        type: SchemaFilterType.STRING,
      },
    };

    if (categoryId) {
      nextFilters.categoryId = {
        value: [categoryId],
        type: SchemaFilterType.STRING,
      };
    }

    if (groupId) {
      nextFilters.groupId = {
        value: [groupId],
        type: SchemaFilterType.STRING,
      };
    }

    if (primaryId) {
      nextFilters.primaryId = {
        value: [primaryId],
        type: SchemaFilterType.STRING,
      };
    }

    onFilter(nextFilters);
    localStorage.setItem('accountsListFilters', JSON.stringify(nextFilters));
  };

  useEffect(() => {
    if (
      menuItems &&
      !prevMenuItems &&
      !accountsCagoriesIsLoading &&
      activePermissions?.includes(PermissionEnum.ACCOUNTS_CHART)
    ) {
      getAccountsStatsAction();
    }
  }, [
    menuItems,
    getAccountsStatsAction,
    prevMenuItems,
    accountsCagoriesIsLoading,
    activePermissions,
  ]);

  useEffect(() => {
    const currentFilters = localStorage.getItem('accountsListFilters');

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'accountsListFilters',
          JSON.stringify({
            ...parsed,
          }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    const currentFilters = localStorage.getItem('accountsListFilters');

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'accountsListFilters',
          JSON.stringify({
            ...parsed,
          }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    if (overdraftEnabled) {
      let nextColumns = (
        [] as {
          name: string;
          label: string;
          hidden: boolean;
          sortable?: boolean;
        }[]
      ).concat(
        columns as {
          name: string;
          label: string;
          hidden: boolean;
          sortable?: boolean;
        }[],
      );

      if (
        overdraftEnabled &&
        !showBalances &&
        !nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns.splice(6, 0, {
          name: 'overdraftEnabled',
          label: 'Overdraft',
          hidden: false,
        });
      } else if (
        !overdraftEnabled &&
        !showBalances &&
        nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns = nextColumns?.filter(
          (item) => item.name !== 'overdraftEnabled',
        );
      }

      setFormattedColumns(nextColumns);
    }
  }, [overdraftEnabled, showBalances]);

  useEffect(() => {
    if (
      filters &&
      !accounts &&
      sort &&
      sort.sortProp &&
      activePermissions?.includes(PermissionEnum.ACCOUNTS_LIST) &&
      accountsStateInititate
    ) {
      const nextFilters = { ...filters };

      if (filters.includes) {
        setShowBalances(true);

        delete nextFilters.includes;
      }

      if (filters?.closingBalancesAt) {
        setClosingBalancesAt(filters.closingBalancesAt.value[0]);

        delete nextFilters.closingBalancesAt;
      }

      getListAction({
        filters: {
          ...nextFilters,
        },
        sort,
      });
    }
  }, [
    filters,
    sort,
    accounts,
    search,
    activePermissions,
    accountsStateInititate,
  ]);

  useEffect(() => {
    if (accounts && !isEqual(prevPagination, pagination)) {
      const body: { checked: boolean; date?: string } = { checked: false };

      if (showBalances) {
        body.checked = showBalances;
      }

      if (closingBalancesAt) {
        body.date = closingBalancesAt;
      }

      onGetAccountsBalances(body);
    }
  }, [accounts, prevPagination, pagination]);

  useEffect(() => {
    getAccountsCategories('all=true&include=primaryAccounts');

    return () => {
      dispatch(clearAccountsState());
    };
  }, []);

  useEffect(() => {
    let nextColumns = (
      [] as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[]
    ).concat(
      columns as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[],
    );

    if (showBalances) {
      currencies?.forEach((item) => {
        nextColumns.push({
          name: item.value as string,
          label: item.suffix as string,
          hidden: false,
          sortable: false,
        });
      });

      if (
        overdraftEnabled &&
        !nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns.splice(6, 0, {
          name: 'overdraftEnabled',
          label: 'Overdraft',
          hidden: false,
        });
      } else if (
        !overdraftEnabled &&
        nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns = nextColumns?.filter(
          (item) => item.name !== 'overdraftEnabled',
        );
      }
    } else {
      nextColumns = columns as {
        name: string;
        label: string;
        hidden: boolean;
        sortable?: boolean;
      }[];

      if (
        overdraftEnabled &&
        !nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns.splice(6, 0, {
          name: 'overdraftEnabled',
          label: 'Overdraft',
          hidden: false,
        });
      } else if (
        !overdraftEnabled &&
        nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns = nextColumns?.filter(
          (item) => item.name !== 'overdraftEnabled',
        );
      }
    }

    setFormattedColumns(nextColumns);
  }, [currencies, showBalances, overdraftEnabled]);

  return {
    exportIsSuccess,
    exportIsLoading,
    accountsCagoriesIsLoading,
    accountsCategoriesIsFetching,
    isLoading,
    onSort,
    setPage,
    errorList,
    onExportClick,
    filtersComponent,
    onTableFilterChange,
    filters,
    sort,
    isFetching,
    onGetAccountsBalances,
    accountsBalancesIsLoading,
    accountsBalancesIsFetching,
    handleSubmit,
    onMenuCategoryClick,
    onMenuGroupClick,
    onMenuPrimaryAccountClick,
  };
};

export default useListFunctions;
